import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#initSwipeHint();
  }

  #controller = new AbortController();
  #swipeHintTimeout;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    if (this.#swipeHintTimeout) {
      window.clearTimeout(this.#swipeHintTimeout);
      this.#swipeHintTimeout = undefined;
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  #initSwipeHint() {
    const hint = this.querySelector('[data-swipe-hint]');
    if (!hint) return;
    if (this.media?.length <= 1) return;

    const prefersTouch = window.matchMedia('(hover: none) and (max-width: 749px)');
    if (!prefersTouch.matches) {
      this.dataset.swipeInteracted = 'true';
      return;
    }

    const { signal } = this.#controller;

    this.addEventListener('pointerdown', this.#markSwipeInteracted, { once: true, signal });
    this.addEventListener('touchstart', this.#markSwipeInteracted, { once: true, signal });

    const handleSelect = (event) => {
      if (event.detail?.userInitiated === false) return;
      this.#markSwipeInteracted();
    };
    this.addEventListener(SlideshowSelectEvent.eventName, handleSelect, { once: true, signal });

    this.#swipeHintTimeout = window.setTimeout(() => {
      this.#markSwipeInteracted();
    }, 4800);
  }

  #markSwipeInteracted = () => {
    if (this.dataset.swipeInteracted === 'true') return;
    this.dataset.swipeInteracted = 'true';
    if (this.#swipeHintTimeout) {
      window.clearTimeout(this.#swipeHintTimeout);
      this.#swipeHintTimeout = undefined;
    }
  };
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
